import Bar from "../states/Bar";

// The white panel every detail block sits in, matching DetailBlock.
const Block = ({ children }) => (
  <div className="mb-3 rounded-lg border border-light_gray bg-white p-4 shadow-card">
    {children}
  </div>
);

const Heading = () => (
  <div className="mb-2 flex h-4 items-center">
    <Bar w={92} h={10} />
  </div>
);

const Row = () => (
  <div className="flex items-center justify-between py-0.5">
    <Bar w={110} h={11} />
    <Bar w={78} h={11} />
  </div>
);

/**
 * Stands in for the whole detail page while it loads.
 *
 * Mirrors the real layout column for column — header, photo frame, filmstrip,
 * spec strip, then the two stacks of blocks — so the page does not visibly
 * rearrange itself the moment the listing arrives.
 */
const UpcomingListingDetailSkeleton = () => (
  <div className="pb-10">
    {/* Header: back link, title, and the position pill with its three buttons */}
    <div className="mb-4 flex flex-wrap items-end justify-between gap-3">
      <div>
        <div className="flex h-5 items-center">
          <Bar w={168} h={11} />
        </div>
        <div className="flex h-10 items-center">
          <Bar w={280} h={26} />
        </div>
      </div>
      <div className="flex items-center gap-2">
        <Bar w={58} h={26} r={2} />
        <Bar w={86} h={32} r={6} />
        <Bar w={86} h={32} r={6} />
        <Bar w={104} h={32} r={6} />
      </div>
    </div>
    <div className="mb-6 block w-full border-b-2 border-primary" />

    <div className="flex flex-col gap-4 lg:flex-row">
      <div className="min-w-0 lg:flex-[1.25]">
        {/* Photo frame, then the filmstrip beneath it */}
        <Bar w="100%" h={288} r={8} />
        <div className="mt-2 flex gap-2">
          {Array.from({ length: 6 }, (_, i) => (
            <Bar key={i} w={80} h={56} r={6} />
          ))}
        </div>

        {/* Spec strip: six figures across one bordered row */}
        <div className="my-3 flex overflow-hidden rounded-lg border border-light_gray bg-white shadow-card">
          {Array.from({ length: 6 }, (_, i) => (
            <div
              key={i}
              className="flex flex-1 flex-col items-center gap-1.5 border-r border-light_gray px-2 py-3 last:border-r-0"
            >
              <Bar w={46} h={18} />
              <Bar w={34} h={9} />
            </div>
          ))}
        </div>

        <Block>
          <Heading />
          <Row />
          <Row />
          <Row />
          <Row />
        </Block>

        <Block>
          <Heading />
          <Bar w="100%" h={11} />
          <div className="mt-1.5">
            <Bar w="72%" h={11} />
          </div>
        </Block>

        <Block>
          <Heading />
          <div className="flex flex-wrap gap-2">
            {[64, 88, 72, 96, 58].map((w, i) => (
              <Bar key={i} w={w} h={24} r={2} />
            ))}
          </div>
        </Block>
      </div>

      <div className="min-w-0 lg:flex-1">
        {/* Hero: go-live badge, price, address, price per square foot */}
        <Block>
          <Bar w={132} h={22} r={2} />
          <div className="mt-2 flex h-9 items-center">
            <Bar w={180} h={28} />
          </div>
          <Bar w="78%" h={12} />
          <div className="mt-1.5">
            <Bar w="56%" h={10} />
          </div>
        </Block>

        <Block>
          <Heading />
          <Bar w="66%" h={11} />
          <div className="mt-1.5">
            <Bar w="58%" h={11} />
          </div>
        </Block>

        <Block>
          <Heading />
          <Bar w="100%" h={200} r={2} />
        </Block>

        <Block>
          <Heading />
          <div className="flex items-start gap-3">
            <Bar w={36} h={36} r={18} />
            <div className="flex-1">
              <Bar w={128} h={12} />
              <div className="mt-1.5">
                <Bar w="72%" h={10} />
              </div>
              <div className="mt-1.5">
                <Bar w="52%" h={9} />
              </div>
            </div>
          </div>
        </Block>
      </div>
    </div>
  </div>
);

export default UpcomingListingDetailSkeleton;
